'use client';

import { useEffect } from 'react';
import { isGermany, isDK } from 'utils';
import { useCartAddressFormStore } from 'stores/cart';
import { CartAddressFields } from './CartAddressFields';
import {
  getInitialDestinationTypeFromSavedAddresses,
  getSavedAddressForDestinationType,
  resolveDestinationType
} from './utils';
import { DESTINATION_TYPES } from '../../constants';
import type { AddressFormSectionClientProps } from '../../types';

/**
 * Selects the correct saved shipping address and renders its form fields.
 * @param props - Saved shipping and pickup-point addresses plus localized form constants.
 * @returns The address fields configured for the saved destination type.
 */
export function AddressFormSectionClient({
  shippingAddress,
  pickupPointAddress,
  initialPickupPoints,
  locale,
  constants
}: AddressFormSectionClientProps) {
  const destinationType = useCartAddressFormStore(state => state.destinationType);
  const isDestinationTypeInitialized = useCartAddressFormStore(state => state.isDestinationTypeInitialized);
  const setDestinationType = useCartAddressFormStore(state => state.setDestinationType);
  const setDestinationTypeInitialized = useCartAddressFormStore(state => state.setDestinationTypeInitialized);
  const fieldErrors = useCartAddressFormStore(state => state.fieldErrors);
  const clearShippingFieldError = useCartAddressFormStore(state => state.clearShippingFieldError);
  const supportsPickupPoints = isGermany(locale) || isDK(locale);
  const savedDestinationType = getInitialDestinationTypeFromSavedAddresses(
    supportsPickupPoints,
    pickupPointAddress,
    shippingAddress
  );
  const resolvedDestinationType = resolveDestinationType(
    isDestinationTypeInitialized,
    destinationType,
    savedDestinationType
  );
  const isPickupPoint = supportsPickupPoints && resolvedDestinationType === DESTINATION_TYPES.PICKUP_POINT;
  const selectedSavedAddress = getSavedAddressForDestinationType(
    resolvedDestinationType,
    pickupPointAddress,
    shippingAddress
  );

  // Apply the saved destination once during hydration, then preserve the user's subsequent selection.
  useEffect(() => {
    if (savedDestinationType && !isDestinationTypeInitialized) {
      setDestinationType(savedDestinationType);
    }

    setDestinationTypeInitialized();
  }, [isDestinationTypeInitialized, savedDestinationType, setDestinationType, setDestinationTypeInitialized]);

  return (
    <CartAddressFields
      address={selectedSavedAddress}
      locale={locale}
      company={resolvedDestinationType === DESTINATION_TYPES.COMPANY}
      constants={constants}
      pickupPoint={isPickupPoint}
      initialPickupPoints={initialPickupPoints}
      onFieldChange={clearShippingFieldError}
      fieldErrors={fieldErrors}
    />
  );
}
